// src/admin/Dashboard.tsx — FINAL CLEAN & POLISHED VERSION
// Only developed RTP sections + real-time sync, toasts, better UX

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import useAdmin from './hooks/useAdmin';
import Sidebar from './components/Sidebar';
import ConfirmModal from '../shared/components/ConfirmModal';
import { Loader2, Wallet } from 'lucide-react';
import { useConfirmModal } from './utils/modal';

// All developed pages (RTP integrated)
import CreateUser from './pages/CreateUser';
import EditUser from './pages/EditUser';
import CreateManager from './pages/CreateManager';
import AddCredits from './pages/AddCredits';
import AssignManagerCredits from './pages/AssignManagerCredits';
import Cashouts from './pages/Cashouts';
import ManageUsers from './pages/ManageUsers';
import ManageManagers from './pages/ManageManagers';
import ManageAdmins from './pages/ManageAdmins';
import CreateTable from './pages/CreateTable';
import ManageTables from './pages/ManageTables';
import ChangePassword from './pages/ChangePassword';
import RtpControl from './pages/RtpControl';
import GlobalRtpControl from './pages/GlobalRtpControl';
import CreditDistributionHistory from './pages/CreditDistributionHistory';
import CreateAdmin from './pages/CreateAdmin';
import VerificationRequests from './pages/VerificationRequests';
import PlayerLifetimeRTP from './pages/PlayerLifetimeRTP';
import ComplianceLogs from './pages/ComplianceLogs';
import VolatilityControl from './pages/VolatilityControl';
import TimeRTPSchedule from './pages/TimeRTPSchedule';
import GameMonitor from './pages/GameMonitor';
import Account from './pages/Account';

const sectionTitles: { [key: string]: string } = {
  'create-user': 'Create User',
  'edit-user': 'Edit User',
  'create-manager': 'Create Manager',
  'add-credits': 'Add/Remove Credits',
  'assign-manager-credits': 'Assign Manager Credits',
  'cashouts': 'Cashouts',
  'manage-users': 'Manage Users',
  'manage-managers': 'Manage Managers',
  'manage-admins': 'Manage Admins',
  'create-table': 'Create Table',
  'manage-tables': 'Manage Tables',
  'change-password': 'Change Password',
  'rtp-control': 'RTP Settings & Control',
  'global-rtp': 'Global RTP (Auto Tables)',
  'credit-distribution-history': 'Credits Distribution History',
  'create-admin': 'Create Admin (Super Admin Only)',
  'verification-requests': 'Verification Requests',
  'player-lifetime-rtp': 'Player Lifetime RTP',
  'compliance-logs': 'Compliance Logs',
  'volatility-control': 'Volatility Control',
  'time-rtp-schedule': 'Time RTP Schedule',
  'game-monitor': 'Game Monitor',
  account: 'Account',
};

export default function Dashboard() {
  const { token, validateToken, refreshCsrf, error, setError, logout, socket } = useAdmin();
  const { isOpen, message, showConfirmModal, handleConfirm, handleCancel } = useConfirmModal();
  const [loading, setLoading] = useState(true);
  const [adminInfo, setAdminInfo] = useState<any>(() => {
    try { return JSON.parse(localStorage.getItem('adminInfo') || 'null'); } catch { return null; }
  });
  const [accountStats, setAccountStats] = useState<any>(null);
  const location = useLocation();

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const isValid = await validateToken();
      if (isValid) await refreshCsrf();
      try { setAdminInfo(JSON.parse(localStorage.getItem('adminInfo') || 'null')); } catch { setAdminInfo(null); }
      setLoading(false);
    };
    init();
  }, [validateToken, refreshCsrf]);

  const section = new URLSearchParams(location.search).get('section') || 'manage-tables';

  // Real-time socket listeners — MUST be before any early return (React rules of hooks)
  useEffect(() => {
    if (!socket) return;

    socket.on('rtp-updated', (data: { tableId: string; rtp: number }) => {
      toast(`RTP updated for table ${data.tableId}: ${data.rtp}%`, { duration: 5000 });
      if (['manage-tables', 'rtp-control'].includes(section)) {
        window.dispatchEvent(new CustomEvent('refresh-tables'));
      }
    });

    socket.on('jackpot-win', (data: { winner: string; amount: number; tableId?: string }) => {
      toast.success(`Jackpot won: $${data.amount} by ${data.winner}!`, { duration: 8000 });
      if (['manage-tables'].includes(section)) {
        window.dispatchEvent(new CustomEvent('refresh-tables'));
      }
    });

    socket.on('credits-updated', (data: { userId: string; realCredits: number; demoCredits: number }) => {
      toast.success(`Credits updated for user ${data.userId}`, { duration: 4000 });
      if (['manage-users', 'add-credits', 'assign-manager-credits'].includes(section)) {
        window.dispatchEvent(new CustomEvent('refresh-users'));
      }
    });

    socket.on('table-updated', () => {
      toast('Table updated');
      if (['manage-tables', 'create-table'].includes(section)) {
        window.dispatchEvent(new CustomEvent('refresh-tables'));
      }
    });

    socket.on('cashout-updated', () => {
      toast('Cashout updated');
      if (section === 'cashouts') {
        window.dispatchEvent(new CustomEvent('refresh-cashouts'));
      }
    });

    return () => {
      socket.off('rtp-updated');
      socket.off('jackpot-win');
      socket.off('credits-updated');
      socket.off('table-updated');
      socket.off('cashout-updated');
    };
  }, [socket, section]);

  useEffect(() => {
    const refreshAdminInfo = () => {
      try { setAdminInfo(JSON.parse(localStorage.getItem('adminInfo') || 'null')); } catch { setAdminInfo(null); }
    };
    window.addEventListener('admin-wallet-refresh', refreshAdminInfo);
    return () => window.removeEventListener('admin-wallet-refresh', refreshAdminInfo);
  }, []);

  useEffect(() => {
    const t = localStorage.getItem('adminToken');
    if (!t) return;
    fetch('/admin/account', { headers: { Authorization: `Bearer ${t}` } })
      .then((res) => res.ok ? res.json() : null)
      .then((payload) => {
        if (payload) setAccountStats(payload);
      })
      .catch(() => {});
  }, [section]);

  const handleLogout = () => {
    showConfirmModal('Are you sure you want to logout?', () => {
      logout();
      handleConfirm();
    });
  };

  // Early return AFTER all hooks
  if (loading || !token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <Loader2 className="animate-spin text-indigo-600" size={48} />
        <span className="ml-4 text-xl text-gray-700">Loading Admin Panel...</span>
      </div>
    );
  }

  const renderSection = () => {
    switch (section) {
      case 'create-user':
        return <CreateUser />;
      case 'edit-user':
        return <EditUser />;
      case 'create-manager':
        return <CreateManager />;
      case 'add-credits':
        return <AddCredits />;
      case 'assign-manager-credits':
        return <AssignManagerCredits />;
      case 'cashouts':
        return <Cashouts />;
      case 'manage-users':
        return <ManageUsers />;
      case 'manage-managers':
        return <ManageManagers />;
      case 'manage-admins':
        return <ManageAdmins />;
      case 'create-table':
        return <CreateTable />;
      case 'manage-tables':
        return <ManageTables />;
      case 'change-password':
        return <ChangePassword />;
      case 'rtp-control':
        return <RtpControl />;
      case 'global-rtp':
        return <GlobalRtpControl />;
      case 'credit-distribution-history':
        return <CreditDistributionHistory />;
      case 'create-admin':
        return <CreateAdmin />;
      case 'verification-requests':
        return <VerificationRequests />;
      case 'player-lifetime-rtp':
        return <PlayerLifetimeRTP />;
      case 'compliance-logs':
        return <ComplianceLogs />;
      case 'volatility-control':
        return <VolatilityControl />;
      case 'time-rtp-schedule':
        return <TimeRTPSchedule />;
      case 'game-monitor':
        return <GameMonitor />;
      case 'account':
        return <Account />;
      default:
        return <ManageTables />;
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar currentSection={section} onLogout={handleLogout} />

      <main className="flex-1 p-6 overflow-auto">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-xl flex justify-between items-center shadow-sm">
            <span className="font-medium">{error}</span>
            <button
              onClick={() => setError('')}
              className="ml-4 text-red-700 hover:text-red-900 font-bold text-xl leading-none"
              aria-label="Dismiss error"
            >
              ×
            </button>
          </div>
        )}

        <header className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {sectionTitles[section] || 'Admin Dashboard'}
            </h1>
            {section === 'manage-tables' && (
              <p className="text-sm text-gray-600 mt-1">Manage all tables with real-time RTP control</p>
            )}
            {section === 'cashouts' && (
              <p className="text-sm text-gray-600 mt-1">Review & process cashout requests</p>
            )}
          </div>
          {adminInfo && (
            <div className="flex flex-wrap justify-start gap-3 lg:justify-end">
              <div className="inline-flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-900">
                <Wallet size={20} className="text-emerald-700" />
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">{adminInfo.is_super_admin ? 'Balance' : 'Left Balance'}</div>
                  <div className="text-lg font-bold">{adminInfo.is_super_admin ? 'Unlimited' : `${Number(adminInfo.wallet_balance || 0).toLocaleString()} credits`}</div>
                </div>
              </div>
              <div className="inline-flex items-center rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-amber-700">Total Distributed</div>
                  <div className="text-lg font-bold">{Number(accountStats?.distributed_total || 0).toLocaleString()} credits</div>
                </div>
              </div>
              <a href="/admin/dashboard?section=account" className="bf-glossy-btn inline-flex items-center rounded-lg px-4 py-3 text-sm font-semibold text-white">
                Account
              </a>
            </div>
          )}
        </header>

        <div className="bg-white rounded-xl shadow-lg min-h-[calc(100vh-180px)] overflow-hidden">
          {renderSection()}
        </div>

        <ConfirmModal
          isOpen={isOpen}
          message={message}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      </main>
    </div>
  );
}
